// InventoryPanel — store items with live stock and low-stock alerts; stock in
// (purchases, optionally booked as an expense) and stock out (issued to a
// class/person); movement history per item.
import { useEffect, useState } from 'react';
import { Plus, Trash2, ArrowDownToLine, ArrowUpFromLine, History, Search, AlertTriangle, Download } from 'lucide-react';
import { getInventoryItems, addInventoryItem, deleteInventoryItem, getInventoryTxns, addInventoryTxn } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, Modal, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import { inr, fmtDate, todayISO, downloadCsv } from '../ui/format';

const CATEGORIES = ['Stationery', 'Cleaning', 'Furniture', 'Electronics', 'Sports', 'Lab', 'Uniform', 'Books', 'Kitchen', 'Other'];
const ITEM_EMPTY = { name: '', category: 'Stationery', unit: 'pcs', min_stock: '', location: '', opening_stock: '' };

export default function InventoryPanel() {
  const [items, setItems] = useState([]);
  const [q,     setQ]     = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [itemForm, setItemForm] = useState(null);
  const [txn,   setTxn]   = useState(null);     // { item, kind, qty, unit_cost, party, txn_date, note, record_expense }
  const [hist,  setHist]  = useState(null);     // { item, rows }
  const [msg,   setMsg]   = useState('');

  useEffect(() => { load(); }, [q]);
  async function load() { try { setItems(await getInventoryItems(q.trim())); } catch (err) { setMsg(errText(err)); } }

  async function saveItem(e) {
    e.preventDefault(); setMsg('');
    try {
      await addInventoryItem({ ...itemForm, min_stock: Number(itemForm.min_stock) || 0, opening_stock: Number(itemForm.opening_stock) || 0 });
      setItemForm(null); setMsg('Item added ✅'); load();
    } catch (err) { setMsg(errText(err)); }
  }
  async function saveTxn(e) {
    e.preventDefault(); setMsg('');
    try {
      const r = await addInventoryTxn(txn.item.id, { ...txn, item: undefined, qty: Number(txn.qty), unit_cost: txn.unit_cost === '' ? null : Number(txn.unit_cost) });
      setMsg(`${txn.kind === 'in' ? 'Stock added' : 'Issued'} ✅ — ${txn.item.name} now ${r.stock} ${txn.item.unit}${r.expense_recorded ? ` · ${inr(r.expense_recorded)} booked as an expense` : ''}`);
      setTxn(null); load();
    } catch (err) { setMsg(errText(err)); }
  }
  async function openHistory(item) {
    try { setHist({ item, rows: await getInventoryTxns(item.id) }); } catch (err) { setMsg(errText(err)); }
  }
  async function remove(item) {
    if (!window.confirm(`Delete ${item.name} and its stock history?`)) return;
    try { await deleteInventoryItem(item.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  const low = items.filter(i => i.low);
  const shown = onlyLow ? low : items;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Inventory</h2>
        <Button onClick={() => setItemForm(ITEM_EMPTY)} className="flex items-center gap-1.5"><Plus size={14} /> Add item</Button>
      </div>
      <Msg msg={msg} className="mb-3" />
      {low.length > 0 && (
        <button onClick={() => setOnlyLow(!onlyLow)} className="flex items-center gap-2 text-sm font-semibold text-caution bg-caution-tint px-3 py-2 rounded-s mb-3">
          <AlertTriangle size={15} /> {low.length} item{low.length === 1 ? ' is' : 's are'} running low — {onlyLow ? 'show all' : 'show only these'}
        </button>
      )}

      <Card>
        <CardHeader title={`${shown.length} item${shown.length === 1 ? '' : 's'}`} action={
          <div className="flex gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input className={`${inputCls} pl-8`} placeholder="Search items" value={q} onChange={e => setQ(e.target.value)} aria-label="Search items" />
            </div>
            <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!items.length}
              onClick={() => downloadCsv('inventory.csv', [
                { key: 'name', label: 'Item' }, { key: 'category', label: 'Category' }, { key: 'stock', label: 'Stock' },
                { key: 'unit', label: 'Unit' }, { key: 'min_stock', label: 'Min stock' }, { key: 'location', label: 'Location' },
              ], items)}><Download size={13} /> CSV</Button>
          </div>
        } />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5 text-right">In stock</th><th className="px-4 py-2.5">Location</th><th className="px-4 py-2.5">Last moved</th><th className="px-4 py-2.5"></th></tr></thead>
            <tbody>
              {shown.map(i => (
                <tr key={i.id} className="border-t border-line-soft">
                  <td className="px-4 py-3 font-semibold text-ink">{i.name}</td>
                  <td className="px-4 py-3">{i.category || '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                    <b className={i.low ? 'text-critical' : 'text-ink'}>{i.stock}</b> {i.unit}
                    {i.low && <span className="block"><Badge tone="critical">Low (min {i.min_stock})</Badge></span>}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{i.location || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{i.last_movement ? fmtDate(i.last_movement) : '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <Button variant="secondary" className="!px-2 !py-1 !text-xs inline-flex items-center gap-1 mr-1"
                      onClick={() => setTxn({ item: i, kind: 'in', qty: '', unit_cost: '', party: '', txn_date: todayISO(), note: '', record_expense: true })}>
                      <ArrowDownToLine size={12} /> Stock in
                    </Button>
                    <Button variant="secondary" className="!px-2 !py-1 !text-xs inline-flex items-center gap-1 mr-1" disabled={!i.stock}
                      onClick={() => setTxn({ item: i, kind: 'out', qty: '', unit_cost: '', party: '', txn_date: todayISO(), note: '' })}>
                      <ArrowUpFromLine size={12} /> Issue
                    </Button>
                    <button onClick={() => openHistory(i)} aria-label={`History of ${i.name}`} className="text-ink-soft hover:text-ink p-1"><History size={14} /></button>
                    <button onClick={() => remove(i)} aria-label={`Delete ${i.name}`} className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {!shown.length && <EmptyRow cols={6}>No items yet — click Add item</EmptyRow>}
            </tbody>
          </table>
        </div>
      </Card>

      {itemForm && (
        <Modal title="Add item" onClose={() => setItemForm(null)} width={480}>
          <form onSubmit={saveItem} className="flex flex-col gap-3">
            <Field label="Item name"><input required className={inputCls} placeholder="e.g. Chalk box" value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <input list="inv-cats" className={inputCls} value={itemForm.category} onChange={e => setItemForm({ ...itemForm, category: e.target.value })} />
                <datalist id="inv-cats">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
              </Field>
              <Field label="Unit"><input className={inputCls} placeholder="pcs, box, kg…" value={itemForm.unit} onChange={e => setItemForm({ ...itemForm, unit: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Opening stock"><input type="number" min="0" step="any" className={inputCls} value={itemForm.opening_stock} onChange={e => setItemForm({ ...itemForm, opening_stock: e.target.value })} /></Field>
              <Field label="Alert below"><input type="number" min="0" step="any" className={inputCls} value={itemForm.min_stock} onChange={e => setItemForm({ ...itemForm, min_stock: e.target.value })} /></Field>
              <Field label="Location"><input className={inputCls} placeholder="Store room" value={itemForm.location} onChange={e => setItemForm({ ...itemForm, location: e.target.value })} /></Field>
            </div>
            <Button type="submit" className="w-fit">Add item</Button>
          </form>
        </Modal>
      )}

      {txn && (
        <Modal title={`${txn.kind === 'in' ? 'Stock in' : 'Issue'} — ${txn.item.name}`} onClose={() => setTxn(null)} width={460}>
          <form onSubmit={saveTxn} className="flex flex-col gap-3">
            <p className="text-sm text-ink-soft">In stock now: <b className="text-ink">{txn.item.stock} {txn.item.unit}</b></p>
            <div className="grid grid-cols-2 gap-3">
              <Field label={`Quantity (${txn.item.unit})`}><input required type="number" min="0.01" step="any" max={txn.kind === 'out' ? txn.item.stock : undefined} className={inputCls} value={txn.qty} onChange={e => setTxn({ ...txn, qty: e.target.value })} /></Field>
              <Field label="Date"><input type="date" className={inputCls} value={txn.txn_date} onChange={e => setTxn({ ...txn, txn_date: e.target.value })} /></Field>
            </div>
            {txn.kind === 'in' ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Supplier"><input className={inputCls} value={txn.party} onChange={e => setTxn({ ...txn, party: e.target.value })} /></Field>
                <Field label="Cost per unit (₹)"><input type="number" min="0" step="any" className={inputCls} value={txn.unit_cost} onChange={e => setTxn({ ...txn, unit_cost: e.target.value })} /></Field>
              </div>
            ) : (
              <Field label="Issued to"><input required className={inputCls} placeholder="e.g. Class 5A, Lab, Mr Sharma" value={txn.party} onChange={e => setTxn({ ...txn, party: e.target.value })} /></Field>
            )}
            <Field label="Note"><input className={inputCls} value={txn.note} onChange={e => setTxn({ ...txn, note: e.target.value })} /></Field>
            {txn.kind === 'in' && (
              <label className="flex items-center gap-2 text-sm font-semibold text-ink">
                <input type="checkbox" checked={txn.record_expense} onChange={e => setTxn({ ...txn, record_expense: e.target.checked })} className="accent-[var(--transit)]" />
                Also record the cost in Income &amp; Expenses
                {txn.qty && txn.unit_cost ? <span className="text-ink-soft font-normal">({inr(Number(txn.qty) * Number(txn.unit_cost))})</span> : null}
              </label>
            )}
            <Button type="submit" className="w-fit">{txn.kind === 'in' ? 'Add stock' : 'Issue'}</Button>
          </form>
        </Modal>
      )}

      {hist && (
        <Modal title={`History — ${hist.item.name}`} onClose={() => setHist(null)} width={620}>
          <table className="w-full text-sm">
            <thead><tr className={theadRowCls}><th className="px-3 py-2">Date</th><th className="px-3 py-2">Movement</th><th className="px-3 py-2">Supplier / issued to</th><th className="px-3 py-2">Note</th></tr></thead>
            <tbody>
              {hist.rows.map(t => (
                <tr key={t.id} className="border-t border-line-soft">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(t.txn_date)}</td>
                  <td className={`px-3 py-2 font-bold tabular-nums ${t.kind === 'in' ? 'text-success' : 'text-critical'}`}>{t.kind === 'in' ? '+' : '−'}{t.qty} {hist.item.unit}
                    {t.unit_cost != null && <span className="block text-xs text-ink-soft font-normal">@ {inr(t.unit_cost)}</span>}</td>
                  <td className="px-3 py-2">{t.party || '—'}</td>
                  <td className="px-3 py-2 text-ink-soft">{t.note || ''}</td>
                </tr>
              ))}
              {!hist.rows.length && <EmptyRow cols={4}>No movements yet</EmptyRow>}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}
